// widget.js — Aeshal Assistant chat widget. Vanilla ES2020, no dependencies.
// Attaches an open shadow DOM host to <body>. Endpoint comes from
// document.currentScript.dataset.endpoint. Conversation is kept in
// sessionStorage only (cap 20 messages). All chat text is rendered via
// textContent, never innerHTML, so nothing user- or model-supplied can
// inject markup.
(function () {
  "use strict";

  var CURRENT_SCRIPT = document.currentScript;
  var ENDPOINT = (CURRENT_SCRIPT && CURRENT_SCRIPT.dataset && CURRENT_SCRIPT.dataset.endpoint) || "";
  // The snippet and README ask the owner for the bare service URL, so the /chat
  // path is added here (same convention as test/ask.mjs). A value that already
  // ends in /chat is left alone.
  var CHAT_URL = chatUrl(ENDPOINT);

  function chatUrl(endpoint) {
    var base = String(endpoint || "");
    while (base.length > 0 && base.charAt(base.length - 1) === "/") {
      base = base.slice(0, -1);
    }
    if (!base) return "";
    return base.slice(-5) === "/chat" ? base : base + "/chat";
  }

  var STORAGE_KEY = "aeshal-chat-v1";
  var HISTORY_CAP = 20;
  var SEED_MESSAGE =
    "Assalamu alaikum, welcome. Ask me about sizing, shipping, or made to order. " +
    "For anything personal, a human reads every email at info@aeshal.com.";
  var FALLBACK_LINE =
    "I am having a quiet moment. Please email info@aeshal.com and a human will reply within a day.";

  var BRAND_BG = "#F7F3EB";
  var BRAND_INK = "#26221C";
  var BRAND_ACCENT = "#9A7440";
  var FONT_STACK = 'Jost, "Helvetica Neue", Arial, sans-serif';

  // ---------- state ----------
  var messages = loadHistory(); // [{role:"user"|"assistant", content:string}]
  var isOpen = false;
  var isSending = false;

  // ---------- host + shadow root ----------
  var host = document.createElement("div");
  host.id = "aeshal-assistant-host";
  host.style.position = "fixed";
  host.style.zIndex = "2147483000";
  host.style.bottom = "0";
  host.style.right = "0";
  host.style.left = "auto";
  host.style.top = "auto";
  host.style.width = "0";
  host.style.height = "0";
  document.body.appendChild(host);

  var root = host.attachShadow({ mode: "open" });

  var style = document.createElement("style");
  style.textContent =
    ":host{all:initial;}" +
    "*{box-sizing:border-box;font-family:" + FONT_STACK + ";}" +
    ".launcher{" +
    "position:fixed;bottom:20px;right:20px;display:flex;align-items:center;gap:8px;" +
    "background:" + BRAND_ACCENT + ";color:#fff;border:none;border-radius:999px;" +
    "padding:12px 18px;font-size:15px;font-weight:500;cursor:pointer;" +
    "box-shadow:0 4px 16px rgba(38,34,28,0.25);}" +
    ".launcher:focus-visible{outline:2px solid " + BRAND_INK + ";outline-offset:2px;}" +
    ".launcher svg{width:20px;height:20px;flex:none;}" +
    ".panel{" +
    "position:fixed;bottom:88px;right:20px;width:360px;height:560px;max-height:80vh;" +
    "background:" + BRAND_BG + ";color:" + BRAND_INK + ";border-radius:16px;" +
    "box-shadow:0 12px 40px rgba(38,34,28,0.3);display:flex;flex-direction:column;" +
    "overflow:hidden;}" +
    ".panel[hidden]{display:none;}" +
    ".header{" +
    "padding:16px 18px;border-bottom:1px solid rgba(38,34,28,0.12);flex:none;}" +
    ".header h1{margin:0;font-size:18px;font-weight:500;letter-spacing:0.02em;}" +
    ".header p{margin:4px 0 0;font-size:12px;opacity:0.75;}" +
    ".close-btn{" +
    "position:absolute;top:14px;right:14px;background:none;border:none;cursor:pointer;" +
    "color:" + BRAND_INK + ";font-size:18px;line-height:1;padding:4px;}" +
    ".close-btn:focus-visible{outline:2px solid " + BRAND_ACCENT + ";outline-offset:2px;}" +
    ".messages{flex:1;overflow-y:auto;padding:14px;display:flex;flex-direction:column;gap:10px;}" +
    ".bubble{max-width:85%;padding:9px 12px;border-radius:12px;font-size:14px;line-height:1.4;" +
    "white-space:pre-wrap;word-wrap:break-word;}" +
    ".bubble.assistant{background:#fff;align-self:flex-start;border:1px solid rgba(38,34,28,0.1);}" +
    ".bubble.user{background:" + BRAND_ACCENT + ";color:#fff;align-self:flex-end;}" +
    ".bubble a{color:inherit;}" +
    ".typing{align-self:flex-start;display:flex;gap:4px;padding:9px 12px;}" +
    ".typing span{width:6px;height:6px;border-radius:50%;background:" + BRAND_INK + ";opacity:0.4;" +
    "animation:aeshal-blink 1.2s infinite ease-in-out;}" +
    ".typing span:nth-child(2){animation-delay:0.2s;}" +
    ".typing span:nth-child(3){animation-delay:0.4s;}" +
    "@keyframes aeshal-blink{0%,80%,100%{opacity:0.25;}40%{opacity:0.9;}}" +
    ".composer{flex:none;display:flex;gap:8px;padding:12px;border-top:1px solid rgba(38,34,28,0.12);}" +
    ".composer input{" +
    "flex:1;border:1px solid rgba(38,34,28,0.2);border-radius:999px;padding:9px 14px;" +
    "font-size:14px;background:#fff;color:" + BRAND_INK + ";}" +
    ".composer input:focus-visible{outline:2px solid " + BRAND_ACCENT + ";outline-offset:1px;}" +
    ".composer button{" +
    "border:none;border-radius:999px;background:" + BRAND_ACCENT + ";color:#fff;" +
    "padding:0 16px;font-size:14px;font-weight:500;cursor:pointer;flex:none;}" +
    ".composer button:disabled{opacity:0.5;cursor:default;}" +
    ".composer button:focus-visible{outline:2px solid " + BRAND_INK + ";outline-offset:2px;}" +
    "@media (max-width:640px){" +
    ".panel{" +
    "left:0;right:0;bottom:0;width:100%;height:85vh;max-height:85vh;border-radius:16px 16px 0 0;}" +
    "}" +
    "@media (prefers-reduced-motion:no-preference){" +
    ".panel{transition:transform 0.2s ease,opacity 0.2s ease;}" +
    ".panel.entering{transform:translateY(12px);opacity:0;}" +
    "}" +
    "@media (prefers-reduced-motion:reduce){" +
    ".typing span{animation:none;opacity:0.6;}" +
    "}";
  root.appendChild(style);

  // ---------- launcher ----------
  var launcher = document.createElement("button");
  launcher.className = "launcher";
  launcher.type = "button";
  launcher.setAttribute("aria-haspopup", "dialog");
  launcher.setAttribute("aria-expanded", "false");
  launcher.setAttribute("aria-label", "Chat with Aeshal");
  launcher.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 4h16v12H8l-4 4V4z" ' +
    'stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"/></svg>' +
    "<span>Chat</span>";
  root.appendChild(launcher);

  // ---------- panel ----------
  var panel = document.createElement("div");
  panel.className = "panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-label", "Aeshal chat");
  panel.hidden = true;
  panel.style.position = "fixed";

  var header = document.createElement("div");
  header.className = "header";
  header.style.position = "relative";
  var h1 = document.createElement("h1");
  h1.textContent = "Aeshal";
  var subtitle = document.createElement("p");
  subtitle.textContent = "Our assistant replies in seconds, day or night.";
  var closeBtn = document.createElement("button");
  closeBtn.className = "close-btn";
  closeBtn.type = "button";
  closeBtn.setAttribute("aria-label", "Close chat");
  closeBtn.textContent = "✕";
  header.appendChild(h1);
  header.appendChild(subtitle);
  header.appendChild(closeBtn);

  var messagesEl = document.createElement("div");
  messagesEl.className = "messages";
  messagesEl.setAttribute("role", "log");
  messagesEl.setAttribute("aria-live", "polite");

  var composer = document.createElement("form");
  composer.className = "composer";
  var input = document.createElement("input");
  input.type = "text";
  input.setAttribute("aria-label", "Type your message");
  input.setAttribute("autocomplete", "off");
  input.maxLength = 2000;
  var sendBtn = document.createElement("button");
  sendBtn.type = "submit";
  sendBtn.textContent = "Send";
  composer.appendChild(input);
  composer.appendChild(sendBtn);

  panel.appendChild(header);
  panel.appendChild(messagesEl);
  panel.appendChild(composer);
  root.appendChild(panel);

  // ---------- rendering ----------
  function renderMessage(msg) {
    var bubble = document.createElement("div");
    bubble.className = "bubble " + (msg.role === "user" ? "user" : "assistant");
    bubble.textContent = msg.content;
    messagesEl.appendChild(bubble);
    return bubble;
  }

  function renderFallback() {
    var bubble = document.createElement("div");
    bubble.className = "bubble assistant";
    var lead = document.createTextNode(
      "I am having a quiet moment. Please email "
    );
    var link = document.createElement("a");
    link.href = "mailto:info@aeshal.com";
    link.textContent = "info@aeshal.com";
    var tail = document.createTextNode(" and a human will reply within a day.");
    bubble.appendChild(lead);
    bubble.appendChild(link);
    bubble.appendChild(tail);
    messagesEl.appendChild(bubble);
    scrollToBottom();
  }

  function renderAll() {
    messagesEl.textContent = "";
    if (messages.length === 0) {
      renderMessage({ role: "assistant", content: SEED_MESSAGE });
    } else {
      for (var i = 0; i < messages.length; i++) {
        renderMessage(messages[i]);
      }
    }
    scrollToBottom();
  }

  function scrollToBottom() {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  var typingEl = null;
  function showTyping() {
    typingEl = document.createElement("div");
    typingEl.className = "typing";
    typingEl.setAttribute("aria-label", "Assistant is typing");
    for (var i = 0; i < 3; i++) {
      typingEl.appendChild(document.createElement("span"));
    }
    messagesEl.appendChild(typingEl);
    scrollToBottom();
  }
  function hideTyping() {
    if (typingEl && typingEl.parentNode) {
      typingEl.parentNode.removeChild(typingEl);
    }
    typingEl = null;
  }

  // ---------- storage ----------
  function loadHistory() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (m) {
        return (
          m &&
          (m.role === "user" || m.role === "assistant") &&
          typeof m.content === "string"
        );
      });
    } catch (e) {
      return [];
    }
  }

  // The server rejects a payload whose first message is not from the user, so
  // take the last HISTORY_CAP messages and drop any leading assistant turns.
  function historyWindow() {
    var out = messages.slice(-HISTORY_CAP);
    while (out.length > 0 && out[0].role !== "user") {
      out.shift();
    }
    return out;
  }

  function saveHistory() {
    try {
      var trimmed = historyWindow();
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch (e) {
      // sessionStorage unavailable (private mode, quota) — fail silently.
    }
  }

  // ---------- open / close ----------
  var lastFocused = null;

  function openPanel() {
    isOpen = true;
    panel.hidden = false;
    launcher.setAttribute("aria-expanded", "true");
    // Inside a shadow root document.activeElement retargets to the host, so
    // ask the shadow root for the element that actually had focus.
    lastFocused = root.activeElement || document.activeElement;
    renderAll();
    document.addEventListener("keydown", onKeydown, true);
    // Focus after paint so hidden->visible transition doesn't eat focus.
    window.setTimeout(function () {
      input.focus();
    }, 0);
  }

  function closePanel() {
    isOpen = false;
    panel.hidden = true;
    launcher.setAttribute("aria-expanded", "false");
    document.removeEventListener("keydown", onKeydown, true);
    // The host div and <body> are not real focus targets, so fall back to the
    // launcher, which is where focus belongs after a dialog closes.
    var target = launcher;
    if (
      lastFocused &&
      lastFocused !== host &&
      lastFocused !== document.body &&
      typeof lastFocused.focus === "function"
    ) {
      target = lastFocused;
    }
    target.focus();
  }

  function onKeydown(e) {
    if (e.key === "Escape" || e.key === "Esc") {
      e.preventDefault();
      closePanel();
      return;
    }
    if (e.key === "Tab") {
      trapFocus(e);
    }
  }

  function trapFocus(e) {
    var focusable = panel.querySelectorAll(
      'button, input, a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = root.activeElement;
    if (e.shiftKey) {
      if (active === first || active === panel) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  launcher.addEventListener("click", function () {
    if (isOpen) {
      closePanel();
    } else {
      openPanel();
    }
  });
  closeBtn.addEventListener("click", closePanel);

  // ---------- sending ----------
  function setSending(sending) {
    isSending = sending;
    input.disabled = sending;
    sendBtn.disabled = sending;
  }

  async function sendMessage(text) {
    messages.push({ role: "user", content: text });
    saveHistory();
    renderMessage({ role: "user", content: text });
    scrollToBottom();

    setSending(true);
    showTyping();

    try {
      if (!CHAT_URL) throw new Error("no endpoint configured");
      var res = await fetch(CHAT_URL, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ messages: historyWindow() }),
      });
      if (!res.ok) throw new Error("bad status " + res.status);
      var data = await res.json();
      var reply =
        data && typeof data.reply === "string" && data.reply.trim()
          ? data.reply
          : FALLBACK_LINE;
      hideTyping();
      messages.push({ role: "assistant", content: reply });
      saveHistory();
      renderMessage({ role: "assistant", content: reply });
      scrollToBottom();
    } catch (err) {
      hideTyping();
      renderFallback();
    } finally {
      setSending(false);
      input.focus();
    }
  }

  composer.addEventListener("submit", function (e) {
    e.preventDefault();
    if (isSending) return;
    var text = input.value.trim();
    if (!text) return;
    input.value = "";
    sendMessage(text);
  });
})();
